import type { Comment } from '@common/schemas/comment.schema';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

interface ReaderFeedbackDrawerProps {
  comments: Comment[];
  onAddComment: (text: string) => Promise<void>;
  onAddReply: (commentId: string, text: string) => Promise<void>;
  onDeleteComment: (commentId: string) => Promise<void>;
  chapterName: string;
  onClose: () => void;
  canManageAllComments: boolean;
}

export function ReaderFeedbackDrawer({
  comments,
  onAddComment,
  onAddReply,
  onDeleteComment,
  chapterName,
  onClose,
  canManageAllComments,
}: ReaderFeedbackDrawerProps) {
  const { t } = useTranslation();
  const [newCommentText, setNewCommentText] = useState('');
  const [replyTextMap, setReplyTextMap] = useState<Record<string, string>>({});
  const [activeReplyId, setActiveReplyId] = useState<string | null>(null);

  const handlePostComment = async () => {
    if (!newCommentText.trim()) return;
    await onAddComment(newCommentText);
    setNewCommentText('');
  };

  const handlePostReply = async (commentId: string) => {
    const text = replyTextMap[commentId];
    if (!text?.trim()) return;
    await onAddReply(commentId, text);
    setReplyTextMap((prev) => ({ ...prev, [commentId]: '' }));
    setActiveReplyId(null);
  };

  return (
    <Box sx={{ width: { xs: '100vw', sm: 380 }, height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.paper', borderLeft: '1px solid', borderColor: 'divider' }}>
      {/* Header */}
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
            {t('reader.feedback.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {chapterName}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Comment List */}
      <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {comments.map((comment) => (
          <Paper key={comment.id} elevation={0} sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, bgcolor: 'background.default' }}>
            {/* Header info */}
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 800 }}>
                  {comment.authorName}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {new Date(comment.audit.createdAt).toLocaleDateString()}
                </Typography>
              </Box>
              {canManageAllComments && (
                <IconButton size="small" color="error" onClick={() => onDeleteComment(comment.id)}>
                  <DeleteIcon sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Stack>

            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: 'text.primary', mb: 1.5 }}>
              {comment.content}
            </Typography>

            {/* Replies */}
            {comment.replies && comment.replies.length > 0 && (
              <Stack spacing={1} sx={{ pl: 2, borderLeft: '2px solid', borderColor: 'divider', mb: 1.5 }}>
                {comment.replies.map((reply) => (
                  <Box key={reply.id}>
                    <Typography variant="caption" sx={{ fontWeight: 800 }}>
                      {reply.authorName}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.825rem' }}>
                      {reply.content}
                    </Typography>
                  </Box>
                ))}
              </Stack>
            )}

            {/* Inline reply action */}
            {activeReplyId === comment.id ? (
              <Stack spacing={1} sx={{ mt: 1.5 }}>
                <TextField
                  size="small"
                  multiline
                  rows={2}
                  placeholder={t('reader.feedback.reply_placeholder')}
                  value={replyTextMap[comment.id] || ''}
                  onChange={(e) => setReplyTextMap((prev) => ({ ...prev, [comment.id]: e.target.value }))}
                />
                <Stack direction="row" spacing={1} justifyContent="flex-end">
                  <Button size="small" variant="text" onClick={() => setActiveReplyId(null)}>
                    {t('common.cancel')}
                  </Button>
                  <Button size="small" variant="contained" onClick={() => handlePostReply(comment.id)}>
                    {t('reader.feedback.reply_submit')}
                  </Button>
                </Stack>
              </Stack>
            ) : (
              <Button size="small" variant="text" sx={{ p: 0, textTransform: 'none' }} onClick={() => setActiveReplyId(comment.id)}>
                {t('reader.feedback.reply')}
              </Button>
            )}
          </Paper>
        ))}

        {comments.length === 0 && (
          <Box sx={{ py: 6, textCenter: 'center', opacity: 0.5, textAlign: 'center' }}>
            <Typography variant="body2">
              {t('reader.feedback.empty')}
            </Typography>
          </Box>
        )}
      </Box>

      {/* Input panel at bottom */}
      <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 1, fontWeight: 700 }}>
          {t('reader.feedback.input_label')}
        </Typography>
        <Stack spacing={1}>
          <TextField
            multiline
            rows={3}
            fullWidth
            placeholder={t('reader.feedback.placeholder')}
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
          />
          <Button variant="contained" fullWidth onClick={handlePostComment}>
            {t('reader.feedback.submit')}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
